/**
 * Execution Center — 执行中心
 *
 * 任务编排、执行状态追踪、操作控制
 * 风格: 暗夜蓝 + 萤火暖金
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, Tag, message } from 'antd';
import { executionAPI } from '../api/execution';

// 状态配置
const STATUS_CONFIG = {
  completed: { label: '已完成', color: '#34d399', bg: 'rgba(52,211,153,0.1)' },
  running: { label: '执行中', color: '#E8D28A', bg: 'rgba(232,210,138,0.1)' },
  pending: { label: '等待中', color: '#A8C7D8', bg: 'rgba(168,199,216,0.1)' },
  failed: { label: '失败', color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
  paused: { label: '已暂停', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
  waiting: { label: '等待触发', color: '#818cf8', bg: 'rgba(129,140,248,0.1)' },
};

export default function ExecutionCenter() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      let data;
      try {
        data = await executionAPI.getTasks();
      } catch {
        data = await executionAPI.mockGetTasks();
      }
      setTasks(data);
    } catch {
      message.error('加载任务失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (taskId) => {
    message.info('任务已暂停');
  };

  const handleCancel = async (taskId) => {
    setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
    message.success('任务已取消');
  };

  const handleRetry = async (taskId) => {
    setTasks((prev) =>
      prev.map((t) => t.taskId === taskId ? { ...t, status: 'running', progress: 0 } : t)
    );
    message.info('任务重试中...');
  };

  // 统计数据
  const stats = {
    total: tasks.length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    running: tasks.filter((t) => t.status === 'running').length,
    pending: tasks.filter((t) => ['pending', 'waiting'].includes(t.status)).length,
  };

  return (
    <div style={styles.pageShell}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseSoft {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <span style={{ fontSize: 28, marginRight: 12 }}>🚀</span>
            Execution Center
          </h1>
          <p style={styles.subtitle}>
            上海三日旅行 · 任务编排与事务执行
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={styles.statusBadge}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 8px rgba(52,211,153,0.4)', animation: 'pulseSoft 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 13, color: '#34d399', fontWeight: 500 }}>执行中</span>
          </div>
        </div>
      </header>

      {/* ── Stats ── */}
      <div style={styles.statsRow}>
        {[
          { label: '总任务', value: stats.total, icon: '📋', color: '#A8C7D8' },
          { label: '已完成', value: stats.completed, icon: '✅', color: '#34d399' },
          { label: '执行中', value: stats.running, icon: '⚡', color: '#E8D28A' },
          { label: '等待中', value: stats.pending, icon: '⏳', color: '#818cf8' },
        ].map((s) => (
          <div key={s.label} style={styles.statCard}>
            <span style={{ fontSize: 18 }}>{s.icon}</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: s.color, marginTop: 4 }}>{s.value}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Task Timeline ── */}
      <div style={styles.timelineCard}>
        <div style={styles.timelineHeader}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            执行时间线
          </h3>
          <Button size="small" onClick={loadTasks} style={styles.refreshBtn}>
            ↻ 刷新
          </Button>
        </div>

        <div style={styles.taskList}>
          {tasks.map((task, idx) => {
            const statusConf = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
            return (
              <div key={task.taskId} style={styles.taskItem}>
                {/* 状态指示器 */}
                <div style={styles.taskStatusCol}>
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: statusConf.bg,
                    border: `2px solid ${statusConf.color}`,
                    fontSize: 14,
                    boxShadow: task.status === 'running' ? `0 0 16px ${statusConf.color}30` : 'none',
                    animation: task.status === 'running' ? 'pulseSoft 1.5s ease-in-out infinite' : 'none',
                  }}>
                    {task.status === 'completed' ? '✓' : task.status === 'running' ? '⚡' : '○'}
                  </div>
                  {idx < tasks.length - 1 && (
                    <div style={{
                      width: 2,
                      flex: 1,
                      minHeight: 20,
                      background: task.status === 'completed' ? 'rgba(52,211,153,0.2)' : 'var(--border-subtle)',
                      margin: '4px auto',
                    }} />
                  )}
                </div>

                {/* 任务内容 */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={styles.taskTime}>{task.time}</span>
                      <span style={styles.taskTitle}>{task.title}</span>
                      <Tag color="default" style={{ fontSize: 10, color: statusConf.color, borderColor: statusConf.color }}>
                        {task.agent}
                      </Tag>
                    </div>
                    <span style={{
                      fontSize: 11,
                      padding: '3px 10px',
                      borderRadius: 'var(--radius-pill)',
                      background: statusConf.bg,
                      color: statusConf.color,
                      fontWeight: 500,
                    }}>
                      {statusConf.label}
                    </span>
                  </div>
                  <p style={styles.taskDetail}>{task.detail}</p>

                  {/* 进度条 */}
                  {task.status === 'running' && (
                    <div style={styles.progressBar}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${task.progress}%`,
                      }} />
                    </div>
                  )}

                  {/* 操作按钮 */}
                  {(task.status === 'running' || task.status === 'failed') && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {task.status === 'running' && (
                        <button onClick={() => handlePause(task.taskId)} style={styles.actionBtn}>
                          暂停
                        </button>
                      )}
                      {task.status === 'failed' && (
                        <button onClick={() => handleRetry(task.taskId)} style={{...styles.actionBtn, color: '#E8D28A', borderColor: '#E8D28A'}}>
                          重试
                        </button>
                      )}
                      <button onClick={() => handleCancel(task.taskId)} style={{...styles.actionBtn, color: '#f87171', borderColor: '#f8717140'}}>
                        取消
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Active Agent ── */}
      <div style={styles.agentRunningCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 20, animation: 'pulseSoft 1.5s ease-in-out infinite' }}>⚡</div>
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Agent 正在执行…
            </h4>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              酒店预约处理中。系统正在验证支付，完成后自动继续餐厅预约。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  pageShell: {
    minHeight: '100vh',
    /* 透出 #root::before 的森林背景（登录页原图） */
    background: 'transparent',
    padding: '32px 36px 80px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginTop: 6,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 16px',
    borderRadius: 'var(--radius-pill)',
    background: 'rgba(52,211,153,0.06)',
    border: '1px solid rgba(52,211,153,0.2)',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
    marginBottom: 24,
    animation: 'fadeUp var(--duration-slow) var(--ease) 100ms both',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '18px 12px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
  },
  timelineCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '24px',
    marginBottom: 20,
    animation: 'fadeUp var(--duration-slow) var(--ease) 200ms both',
  },
  timelineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  refreshBtn: {
    background: 'var(--glass-sm)',
    border: '1px solid var(--border-default)',
    borderRadius: 8,
    color: 'var(--text-secondary)',
    fontSize: 12,
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
  },
  taskItem: {
    display: 'flex',
    gap: 16,
    paddingBottom: 8,
  },
  taskStatusCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 36,
  },
  taskTime: {
    fontSize: 12,
    color: 'var(--accent)',
    fontFamily: 'var(--font-mono)',
    fontWeight: 600,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  taskDetail: {
    fontSize: 12,
    color: 'var(--text-muted)',
    marginTop: 4,
    marginBottom: 0,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    background: 'var(--glass-sm)',
    marginTop: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    background: 'linear-gradient(90deg, var(--accent), #34d399)',
    transition: 'width var(--duration-normal) var(--ease)',
  },
  actionBtn: {
    fontSize: 12,
    padding: '4px 14px',
    borderRadius: 8,
    background: 'transparent',
    border: '1px solid var(--border-default)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease)',
    fontFamily: 'inherit',
  },
  agentRunningCard: {
    padding: '20px 24px',
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(232,210,138,0.15)',
    animation: 'fadeUp var(--duration-slow) var(--ease) 300ms both',
  },
};
